import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { formatPrice } from "./shared";

const STATUS_STYLES = {
  connected: "bg-emerald-500/15 text-emerald-400 border-emerald-500/40",
  onboarding: "bg-amber-500/15 text-amber-400 border-amber-500/40",
  not_started: "bg-white/5 text-white/40 border-white/20",
};
const STATUS_LABELS = { connected: "Connected", onboarding: "Onboarding incomplete", not_started: "Not started" };

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(undefined, { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch {
    return iso;
  }
}

export default function PayoutsPanel() {
  const [data, setData] = useState(null);
  const [settling, setSettling] = useState(null);

  const load = () => {
    api.get("/admin/payouts")
      .then((r) => setData(r.data))
      .catch((e) => toast.error(formatApiError(e)));
  };
  useEffect(() => { load(); }, []);

  const settle = async (p) => {
    if (!window.confirm(`Mark ${formatPrice(p.owed)} owed to ${p.name} as settled (paid outside Stripe)? This cannot be undone.`)) return;
    setSettling(p.id);
    try {
      const r = await api.post(`/admin/payouts/${p.id}/settle`);
      toast.success(`Settled ${formatPrice(r.data.settled_amount)} across ${r.data.settled_count} sale${r.data.settled_count !== 1 ? "s" : ""}`);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSettling(null);
    }
  };

  if (!data) return <p className="text-white/50 text-sm py-8">Loading payouts…</p>;

  const { photographers, transfers, totals } = data;

  return (
    <div className="space-y-12" data-testid="payouts-panel">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="payouts-totals">
        <div className="border border-white/10 p-5">
          <p className="text-xs uppercase tracking-[0.25em] text-white/40">Connected</p>
          <p className="font-serif text-3xl mt-2">{totals?.connected ?? 0}<span className="text-white/30 text-lg"> / {photographers.length}</span></p>
        </div>
        <div className="border border-white/10 p-5">
          <p className="text-xs uppercase tracking-[0.25em] text-white/40">Transfers sent</p>
          <p className="font-serif text-3xl mt-2">{totals?.transfer_count ?? 0}</p>
        </div>
        <div className="border border-white/10 p-5">
          <p className="text-xs uppercase tracking-[0.25em] text-white/40">Auto-paid total</p>
          <p className="font-serif text-3xl mt-2 pm-orange">{formatPrice(totals?.transferred ?? 0)}</p>
        </div>
        <div className="border border-white/10 p-5">
          <p className="text-xs uppercase tracking-[0.25em] text-white/40">Owed (manual)</p>
          <p className="font-serif text-3xl mt-2">{formatPrice(totals?.owed ?? 0)}</p>
        </div>
      </div>
      <div>
        <h3 className="font-serif text-2xl">Stripe Connect status</h3>
        <p className="text-sm text-white/50 mt-1">
          Photographers with an active connection receive their split automatically on every sale.
          "Owed" is earnings from sales made before/without a connection.
        </p>
        <div className="overflow-x-auto mt-6">
          <table className="w-full text-left" data-testid="payouts-photographers-table">
            <thead>
              <tr className="text-xs uppercase tracking-[0.2em] text-white/40 border-b border-white/10">
                <th className="py-3 pr-4 font-semibold">Photographer</th>
                <th className="py-3 pr-4 font-semibold">Stripe</th>
                <th className="py-3 pr-4 font-semibold">Transfers sent</th>
                <th className="py-3 pr-4 font-semibold">Auto-paid</th>
                <th className="py-3 font-semibold">Owed (manual)</th>
              </tr>
            </thead>
            <tbody>
              {photographers.length === 0 && (
                <tr><td colSpan="5" className="py-8 text-white/40 text-sm">No photographers yet.</td></tr>
              )}
              {photographers.map((p) => (
                <tr key={p.id} className="border-b border-white/5 text-sm" data-testid={`payout-row-${p.id}`}>
                  <td className="py-4 pr-4">
                    <p className="text-white">{p.name}</p>
                    <p className="text-white/40 text-xs font-condensed">{p.email}</p>
                  </td>
                  <td className="py-4 pr-4">
                    <span className={`inline-block border px-3 py-1 text-[11px] uppercase tracking-[0.15em] font-semibold ${STATUS_STYLES[p.connect_status]}`}>
                      {STATUS_LABELS[p.connect_status]}
                    </span>
                    {p.stripe_account_id && (
                      <p className="text-white/30 text-[11px] font-mono mt-1.5">{p.stripe_account_id}</p>
                    )}
                  </td>
                  <td className="py-4 pr-4 text-white/70">{p.transfer_count}</td>
                  <td className={`py-4 pr-4 ${p.transferred > 0 ? "pm-orange font-semibold" : "text-white/30"}`}>
                    {p.transferred > 0 ? formatPrice(p.transferred) : "—"}
                  </td>
                  <td className="py-4 text-white/70">
                    {p.owed > 0 ? (
                      <div className="flex items-center gap-3 flex-wrap">
                        <span>{formatPrice(p.owed)} <span className="text-white/40 text-xs">({p.owed_count} sale{p.owed_count !== 1 ? "s" : ""})</span></span>
                        <button
                          onClick={() => settle(p)}
                          disabled={settling === p.id}
                          className="border border-emerald-500/40 text-emerald-400 px-3 py-1.5 text-[11px] uppercase tracking-[0.15em] font-semibold hover:bg-emerald-500/10 transition disabled:opacity-50"
                          data-testid={`settle-button-${p.id}`}
                        >
                          {settling === p.id ? "Settling…" : "Mark settled"}
                        </button>
                      </div>
                    ) : (
                      <span className="text-white/30">—</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div>
        <h3 className="font-serif text-2xl">Payout history</h3>
        <p className="text-sm text-white/50 mt-1">Every automatic Stripe transfer and manual settlement, newest first.</p>
        <div className="overflow-x-auto mt-6">
          <table className="w-full text-left" data-testid="payouts-transfers-table">
            <thead>
              <tr className="text-xs uppercase tracking-[0.2em] text-white/40 border-b border-white/10">
                <th className="py-3 pr-4 font-semibold">When</th>
                <th className="py-3 pr-4 font-semibold">Photographer</th>
                <th className="py-3 pr-4 font-semibold">Photo</th>
                <th className="py-3 pr-4 font-semibold">Amount</th>
                <th className="py-3 pr-4 font-semibold">Method</th>
                <th className="py-3 font-semibold">Stripe transfer</th>
              </tr>
            </thead>
            <tbody>
              {transfers.length === 0 && (
                <tr><td colSpan="5" className="py-8 text-white/40 text-sm" data-testid="payouts-no-transfers">No automatic transfers sent yet.</td></tr>
              )}
              {transfers.map((t) => (
                <tr key={t.id} className="border-b border-white/5 text-sm" data-testid={`transfer-row-${t.id}`}>
                  <td className="py-4 pr-4 text-white/60 whitespace-nowrap">{fmtDate(t.payout_at)}</td>
                  <td className="py-4 pr-4 text-white">{t.photographer_name}</td>
                  <td className="py-4 pr-4 text-white/70">{t.image_title || "—"}</td>
                  <td className="py-4 pr-4 pm-orange font-semibold">{formatPrice(t.photographer_payout)}</td>
                  <td className="py-4 pr-4">
                    <span className={`inline-block border px-2.5 py-1 text-[10px] uppercase tracking-[0.15em] font-semibold ${
                      t.payout_method === "manual"
                        ? "border-sky-500/40 text-sky-400"
                        : "border-emerald-500/40 text-emerald-400"
                    }`}>
                      {t.payout_method === "manual" ? "Manual" : "Stripe"}
                    </span>
                  </td>
                  <td className="py-4 text-white/40 text-xs font-mono">{t.stripe_transfer_id || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
